'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';
import { getConsent, CONSENT_CHANGED, type ConsentChoice } from '@/lib/consent';

// GA4 with Google Consent Mode v2.
//
// Two-stage load to comply with EEA + CCPA:
//   1. The Consent Mode default script always runs and starts in "denied"
//      mode for ad/analytics signals.
//   2. The GA4 gtag.js script loads on every page load (the script itself
//      doesn't track without consent), but no consent_update fires until
//      the user clicks Accept All in the cookie banner.
//   3. When the user picks Essential Only, we leave consent in default
//      denied state — gtag.js exists but won't send hits with personal
//      data; aggregated, cookieless modeled data still flows to GA4.
//
// Tanisha (or Bradley) creates the GA4 property in analytics.google.com
// and sets the Measurement ID via NEXT_PUBLIC_GA_MEASUREMENT_ID. Without
// the env var, this component renders nothing at all (no script tags).

const MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?:      (...args: unknown[]) => void;
  }
}

export function Analytics() {
  const [choice, setChoice] = useState<ConsentChoice | null>(null);

  useEffect(() => {
    setChoice(getConsent());
    function onChange(e: Event) {
      const detail = (e as CustomEvent<ConsentChoice | null>).detail;
      setChoice(detail ?? null);
    }
    window.addEventListener(CONSENT_CHANGED, onChange);
    return () => window.removeEventListener(CONSENT_CHANGED, onChange);
  }, []);

  useEffect(() => {
    if (!MEASUREMENT_ID) return;
    if (typeof window === 'undefined') return;
    if (typeof window.gtag !== 'function') return;
    if (choice === 'accepted') {
      window.gtag('consent', 'update', {
        ad_storage:             'granted',
        ad_user_data:           'granted',
        ad_personalization:     'granted',
        analytics_storage:      'granted',
      });
    } else if (choice === 'essential') {
      window.gtag('consent', 'update', {
        ad_storage:             'denied',
        ad_user_data:           'denied',
        ad_personalization:     'denied',
        analytics_storage:      'denied',
      });
    }
  }, [choice]);

  if (!MEASUREMENT_ID) return null;

  return (
    <>
      <Script id="ga-consent-default" strategy="beforeInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('consent', 'default', {
            ad_storage:           'denied',
            ad_user_data:         'denied',
            ad_personalization:   'denied',
            analytics_storage:    'denied',
            wait_for_update:      500,
          });
          gtag('js', new Date());
        `}
      </Script>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`}
        strategy="afterInteractive"
      />
      <Script id="ga-config" strategy="afterInteractive">
        {`
          gtag('config', '${MEASUREMENT_ID}', {
            anonymize_ip: true,
            send_page_view: true,
          });
        `}
      </Script>
    </>
  );
}
